import React, { useMemo } from "react";

import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ErrorMessage from "../../form/ErrorMessage";
import FieldContainer from "../../form/FieldContainer";
import Label from "../../form/Label";
import UploadZone from "../../form/UploadZone";

import type { JSX } from "react";

import type { ChannelSettingsFieldValues } from "../../../types";

interface Props {
  uploadPercentage: number;
  isUploadSuccess: boolean;
  isUploadError: boolean;
}

export default function ChannelLogoUploader({
  uploadPercentage,
  isUploadSuccess,
  isUploadError,
}: Props): JSX.Element {
  const { t } = useTranslation("editor-fast-channel");
  const { getFieldState, setValue } =
    useFormContext<ChannelSettingsFieldValues>();

  const channelLogoFieldName = "channelLogoFile";
  const [channelLogoFile, channelLogoUrl] = useWatch({
    name: [channelLogoFieldName, "channelLogoUrl"],
  });
  const fieldError = getFieldState(channelLogoFieldName)?.error;

  const channelPicturePreviewUrl = useMemo(() => {
    const tempLogoImage = new Image();

    tempLogoImage.src = channelLogoFile
      ? URL.createObjectURL(channelLogoFile)
      : channelLogoUrl;

    tempLogoImage.onload = (): void => {
      const { width, height } = tempLogoImage;
      const aspectRatio = width / height;

      setValue("channelLogoAspectRatio", aspectRatio);

      if (channelLogoFile) {
        setValue("channelLogoWidth", width);
        setValue("channelLogoHeight", height);
      }
    };

    return tempLogoImage.src;
  }, [channelLogoFile, channelLogoUrl, setValue]);

  const tempPictureStyle = {
    backgroundImage:
      "linear-gradient(45deg, #808080 25%, transparent 25%), linear-gradient(-45deg, #808080 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #808080 75%), linear-gradient(-45deg, transparent 75%, #808080 75%)",
    backgroundSize: "40px 40px",
    backgroundPosition: "0 0, 0 20px, 20px -20px, -20px 0px",
  };

  return (
    <FieldContainer>
      <Label
        label={t("channelLogoLabel")}
        name={channelLogoFieldName}
        sublabel={t("channelLogoSublabel")}
      />
      <div
        className="border-border size-[5.625rem] min-w-fit flex-initial rounded-full border bg-cover"
        style={
          channelPicturePreviewUrl
            ? { backgroundImage: `url(${channelPicturePreviewUrl})` }
            : { ...tempPictureStyle }
        }
      />
      <UploadZone
        withoutPreview
        isUploadError={isUploadError}
        isUploadSuccess={isUploadSuccess}
        name={channelLogoFieldName}
        uploadProgress={uploadPercentage}
      />
      <ErrorMessage error={fieldError} />
    </FieldContainer>
  );
}
